import React, { useState, useMemo } from 'react';
import { Search, type LucideIcon } from 'lucide-react';
import styles from './InfoCard.module.scss';

interface MultiInfoCardProps<T> {
  icon: LucideIcon;
  title: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  previewCount?: number;
  maxHeight?: string;
}

// Handles cards that can hold anywhere from 1 to a dozen+ entries
// (multiple POCs, billing addresses, delivery locations). Shows a
// short preview, then reveals the rest in a scrollable panel so the
// card never blows out the page layout.
export function MultiInfoCard<T extends { id?: string }>({
  icon: Icon,
  title,
  items,
  renderItem,
  previewCount = 2,
  maxHeight = '14rem',
}: MultiInfoCardProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const showSearch = items.length > 5;

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(q));
  }, [items, query]);

  const visible = expanded ? filtered : filtered.slice(0, previewCount);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Icon size={13} />
          {title}
        </div>
        <span className={styles.count}>{items.length}</span>
      </div>

      {expanded && showSearch && (
        <div className={styles.searchWrap}>
          <Search size={12} className={styles.searchIcon} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter"
            className={styles.searchInput}
          />
        </div>
      )}

      <div
        className={
          expanded ? `${styles.list} ${styles.listExpanded}` : styles.list
        }
        // maxHeight is the one genuinely dynamic value here (a prop),
        // so it's the only thing set inline; everything else is in SCSS.
        style={expanded ? { maxHeight } : undefined}
      >
        {visible.length === 0 && (
          <div className={styles.noMatches}>No matches.</div>
        )}
        {visible.map((item, i) => (
          <div key={item.id ?? i} className={styles.item}>
            {renderItem(item)}
          </div>
        ))}
      </div>

      {items.length > previewCount && (
        <button
          onClick={() => {
            setExpanded((e) => !e);
            setQuery('');
          }}
          className={styles.toggle}
        >
          {expanded ? 'Show less' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}
